// Version sans localStorage
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import FieldPalette from './FieldPalette';
import Canvas from './Canvas';
import FieldEditor from './FieldEditor';
import ExportModal from './partials/ExportModal';
import { debug } from './../../../utils';
import { FiArrowLeft } from "react-icons/fi";
import Swal from "sweetalert2";
import './css/formbuilder-admin.css';
import './css/canvas-tab-bar.css';
export default function FormBuilderAdmin({ initialSchema, meta, onSave, customFields = [] }) {
  const defaultSchema = {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    type: "object",
    title: "Formulaire",
    properties: {
      tab_1: {
        type: "object",
        title: "Sans titre",
        properties: {},
        required: []
      }
    }
  };
  const navigate = useNavigate();
  const [schema, setSchema] = useState(initialSchema || defaultSchema);
  const [activeTabId, setActiveTabId] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [showExport, setShowExport] = useState(false);
  useEffect(() => {
    if (!initialSchema) return;
    const clone = structuredClone(initialSchema);
    setSchema(clone);
    if (!clone.properties?.[activeTabId]) {
      const tabs = Object.keys(clone.properties || {});
      setActiveTabId(tabs[0] || null);
    }
  }, [initialSchema]);
  useEffect(() => {
    if (!selectedField) return;
    const tab = schema?.properties?.[activeTabId];
    if (!tab?.properties) return;
    const updated = Object.values(tab.properties).find(f => f.id === selectedField.id);
    if (!updated) {
      setSelectedField(null);
      return;
    }
    if (JSON.stringify(updated) !== JSON.stringify(selectedField)) {
      setSelectedField(updated);
    }
  }, [schema, activeTabId]);
  const validateField = (field, schema, activeTabId) => {
    const activeTab = schema?.properties?.[activeTabId];
    if (!activeTab) return field;
    const name = field.name?.trim();
    if (!name) {
      return {
        ...field,
        error: { name: { cause: "empty", message: "Le nom du champ est obligatoire" } },
        name: selectedField.name,
      };
    }
    if (!/^[a-zA-Z]/.test(name)) {
      return {
        ...field,
        error: { name: { cause: "invalid_format", message: "Le nom doit commencer par une lettre." } },
        name: selectedField.name,
      };
    }
    const duplicate = Object.values(activeTab.properties || {}).some(
      f => f.id !== field.id && f.name?.trim().toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      return {
        ...field,
        error: { name: { cause: "duplicate", message: "Ce nom est déjà utilisé dans cet onglet" } },
        name: selectedField.name,
      };
    }
    return { ...field, error: {} };
  };
  const handleDeleteField = (fieldId) => {
    setSchema(prevSchema => {
      const newSchema = structuredClone(prevSchema);
      const tab = newSchema.properties[activeTabId];
      if (!tab?.properties) return prevSchema;
      const targetKey = Object.keys(tab.properties).find(key => tab.properties[key].id === fieldId);
      if (!targetKey) return prevSchema;
      delete tab.properties[targetKey];
      tab.required = (tab.required || []).filter(r => r !== targetKey);
      return newSchema;
    });
    if (selectedField?.id === fieldId) setSelectedField(null);
  };
  const handleUpdateField = (updatedItem) => {
    if (updatedItem.type === 'form') return;
    let checkedItem = validateField(updatedItem, schema, activeTabId);
    if (checkedItem.error && Object.keys(checkedItem.error).length > 0) {
      setSelectedField(checkedItem);
      return;
    }
    setSchema(prev => {
      const clone = structuredClone(prev);
      const tab = clone.properties?.[activeTabId];
      if (!tab?.properties) return prev;
      const oldKey = selectedField?.name;
      const newKey = checkedItem.name;
      // -------------------------------------------------------
      // 1) Sécurité minimale
      // -------------------------------------------------------
      if (!oldKey || !tab.properties[oldKey]) {
        return prev;
      }
      // -------------------------------------------------------
      // 2) Vérifier doublon SI renommage
      // -------------------------------------------------------
      if (newKey !== oldKey && tab.properties[newKey]) {
        tab.properties[oldKey].error = {
          ...tab.properties[oldKey].error,
          name: "Ce nom est déjà utilisé dans cet onglet",
        };
        return clone;
      }
      // -------------------------------------------------------
      // 3) Mise à jour ou renommage
      // -------------------------------------------------------
      if (newKey === oldKey) {
        Object.assign(tab.properties[oldKey], checkedItem);
      } else {
        tab.properties[newKey] = {
          ...tab.properties[oldKey],
          ...checkedItem,
          name: newKey,
        };
        delete tab.properties[oldKey];
      }
      // -------------------------------------------------------
      // 4) Rebuild REQUIRED depuis properties
      // -------------------------------------------------------
      tab.required = Object.entries(tab.properties)
        .filter(([_, field]) => field["x-required"] === true)
        .map(([key]) => key);
      // -------------------------------------------------------
      // 5) correction des reference si changement de noms
      // -------------------------------------------------------
      if (newKey !== oldKey) {
        renameFieldReferences(clone, oldKey, newKey);
      }
      // -------------------------------------------------------
      // 6) Nettoyage : JSON Schema compliant
      // -------------------------------------------------------
      Object.values(tab.properties).forEach(field => {
        delete field.required;
        delete field.error;
      });
      return clone;
    });
    setSelectedField(checkedItem);
  };
  function renameFieldReferences(schema, oldKey, newKey) {
    const walk = (node) => {
      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }
      if (node && typeof node === 'object') {
        Object.keys(node).forEach(key => {
          const value = node[key];
          // Cas 1 : clé = oldKey (rare mais possible)
          if (key === oldKey) {
            node[newKey] = value;
            delete node[oldKey];
            walk(node[newKey]);
            return;
          }
          // Cas 2 : valeur string = oldKey
          if (typeof value === 'string' && value === oldKey) {
            node[key] = newKey;
            return;
          }
          // Cas 3 : tableaux de références
          if (Array.isArray(value)) {
            node[key] = value.map(v => v === oldKey ? newKey : v);
          }
          walk(value);
        });
      }
    };
    walk(schema);
  }
  const handleAddField = (field) => {
    const baseName = field["x-ui"] || field.type || "field";
    const existingKeys = Object.keys(schema.properties?.[activeTabId]?.properties || {});
    let i = 1;
    let key = `${baseName}_${i}`;
    while (existingKeys.includes(key)) {
      i++;
      key = `${baseName}_${i}`;
    }
    setSchema(prevSchema => {
      const clone = structuredClone(prevSchema);
      const tab = clone.properties[activeTabId];
      if (!tab) return prevSchema;
      // Sécurisation : si properties = [], convertir en {}
      if (Array.isArray(tab.properties)) {
        tab.properties = {};
      }
      // Ajout du champ
      tab.properties[key] = {
        ...field,
        title: field.title || key,
        name: key,
      };
      // Required
      if (field.required) {
        tab.required = [...(tab.required || []), key];
      }
      return clone;
    });
    setSelectedField({ id: key, ...field, name: key, title: field.title || key });
  };
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState('auto');
  useEffect(() => {
    const updateHeight = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top - 20;
      setContainerHeight(`${available}px`);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);
  const toastSuccess = (title = "Sauvegarde effectuée") => {
    Swal.fire({
      toast: true,
      position: "top-end",
      title,
      showConfirmButton: false,
      timer: 1000,
      timerProgressBar: true,
      icon: undefined, // explicite : pas d’icône
      customClass: {
        popup: 'swal2-toast'
      }
    });
  };
  const handleSave = async () => {
    if (!onSave) return;
    await onSave(schema);
    toastSuccess("Formulaire sauvegardé");
  }

  return (
    <div ref={containerRef} className="formbuilder-admin d-flex flex-column w-100" style={{ height: containerHeight }}>
      <div className="border-bottom bg-light py-2 d-flex align-items-center justify-content-between w-100">
        <div className='d-flex align-items-center gap-2'>
          <button
            type="button"
            className="btn btn-outline-secondar btn-smy d-flex align-items-center gap-2"
            onClick={() => { navigate(-1); }}
          >
            <FiArrowLeft size={18} />
          </button>
          {meta && <div className='d-flex align-items-center gap-3'>
            <span className='fw-bold'>{meta.ftName}</span>
            <span className='small bg-light'>{meta.version}</span>
          </div>}
          <div>
          </div>
        </div>
        <div>
          <button
            className="btn btn-sm btn-primary" onClick={handleSave} >
            💾 Sauvegarder
          </button>
          <button className="btn btn-sm btn-outline-secondary ms-2" onClick={() => setShowExport(true)}>
            📤 Exporter
          </button>
        </div>
      </div>
      <div className="flex-grow-1 d-flex">
        <div className="palette-panel border-end bg-light-subtle">
          <FieldPalette onAddField={handleAddField} customFields={customFields} />
        </div>
        <div className="canvas-panel">
          <Canvas
            schema={schema}
            setSchema={setSchema}
            activeTabId={activeTabId}
            setActiveTab={setActiveTabId}
            onActiveTabChange={setActiveTabId}
            onSelectField={setSelectedField}
            selectedField={selectedField}
            onDeleteField={handleDeleteField}
          />

        </div>
        <div className="editor-panel border-start bg-light-subtle">
          <FieldEditor
            selectedField={selectedField}
            onUpdateField={handleUpdateField}
            schema={schema}
            activeTabId={activeTabId}
          />
        </div>
      </div>
      <ExportModal
        show={showExport}
        onClose={() => setShowExport(false)}
        jsonData={schema}
      />
    </div>
  );
}
